import { AppsRoutes } from '../getRoute'
import { PropsWithChildren, MouseEvent } from 'react'
import { RouteProps } from '../types'
import { Params } from '../getParams'
import { getFullRoute, useAppsNavigate } from '../useAppsNavigate'
import { staticState } from '@wellesley/host-remote/common/staticState'

type TargetBlank = '_blank'
type ValidatedTo<T extends TargetBlank | undefined> = T extends undefined
  ? AppsRoutes | ''
  : string

type AppsLinkProps<
  T = RouteProps,
  P extends Params = Params,
  Target extends TargetBlank | undefined = undefined,
> = {
  target?: Target
  to: ValidatedTo<Target>
  props?: T
  queryParams?: P
  className?: string
  disabled?: boolean
  onClick?: (e: MouseEvent<HTMLAnchorElement>) => void
}

export const AppsLink = <
  T extends RouteProps = RouteProps,
  P extends Params = Params,
  Target extends TargetBlank | undefined = undefined,
>({
  to,
  props,
  children,
  target,
  queryParams,
  className,
  disabled,
  onClick,
  ...linkProps
}: PropsWithChildren<AppsLinkProps<T, P, Target>>) => {
  const navigate = useAppsNavigate()
  const fullRoute = getFullRoute(to as AppsRoutes | '', {
    queryParams,
    props,
  })

  const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()

    if (disabled) return

    onClick?.(e)
    navigate(to as AppsRoutes | '', {
      queryParams,
      props,
      target: e.ctrlKey || e.metaKey ? '_blank' : target,
    })
  }

  const { baseUrl } = staticState.get()
  const href = target ? fullRoute : `${baseUrl}${fullRoute}`

  return (
    <a
      href={href}
      target={target}
      onClick={handleClick}
      className={className}
      {...linkProps}
    >
      {children}
    </a>
  )
}
